import type { PlatformFeature } from '../components/home/workflow-text/WorkflowText.types'

// Banner Constants
export const TRUSTED_BY_60_PLUS_BRANDS = 'TRUSTED BY 60+ BRANDS'

// Call to Action Constants
export const GOOD_THINGS_COME_TO_THOSE = 'Good things come to those'
export const WHO_TAKE_ACTION = 'who take action'
export const GET_STARTED = 'Get started'

// Digital Campaigns Constants
export const DIGITAL_CAMPAIGNS_FEATURES = [
  {
    title: 'Smart audiences',
    description: 'Reach Blinkit’s 1P audience with sharper, more precise targeting.',
  },
  {
    title: 'Real-time catalogues',
    description: 'Ads are delivered based on brand stock availability and Blinkit serviceability',
  },
  {
    title: 'Exclusive landing pages',
    description: 'Create custom pages that guide users to complete their purchase.',
  },
]

// FAQs Constants
export const FREQUENTLY_ASKED_QUESTIONS = 'Frequently asked questions'
export const VIEW_ALL_FAQS = 'View All'

export const FAQ_ITEMS = [
  {
    value: 'faq-1',
    question: 'What is MediaMax?',
    answer:
      "MediaMax is Blinkit's self-serve advertising platform that enables brands listed on Blinkit to independently run Meta and Google collaborative ads. Through MediaMax, advertisers can easily select audiences, upload brand catalogues, and create custom landing pages—leveraging Blinkit's 1P commerce data to deliver measurable, performance-driven campaigns.",
  },
  {
    value: 'faq-2',
    question: 'Why should I use MediaMax? How is it different from traditional Meta or Google ads?',
    answer:
      "Unlike traditional ads, MediaMax campaigns are designed for commerce-driven performance. They:\n\n• Serve ads only in regions where the product is in stock (real-time availability).\n• Use Blinkit's first-party audience cohorts for more relevant targeting.\n• Offer end-to-end funnel tracking, from impressions to conversions and GMV, without the need for external pixels or SDKs.\n\nThis makes campaigns more efficient, measurable, and sales-focused.",
  },
  {
    value: 'faq-3',
    question: 'What types of campaigns and ad inventories are available?',
    answer:
      '• Meta: Awareness, Traffic, and Conversion campaigns across Instagram and Facebook placements.\n• Google: Demand Gen and Performance Max (PMax) campaigns across Display, Search, and Shopping inventories.',
  },
  {
    value: 'faq-4',
    question: 'What audience targeting options are available?',
    answer:
      '• Blinkit Category Audiences: Users who have viewed, added to cart, or purchased from specific Blinkit categories.\n• Brand Retargeting Cohorts: Re-engage users who interacted with your brand or products previously.',
  },
  {
    value: 'faq-5',
    question: 'Are there any additional charges to use MediaMax?',
    answer:
      'No. There are no platform or service charges for using MediaMax. You only pay for your ad spends on Meta or Google.',
  },
  {
    value: 'faq-6',
    question: 'What are the ideal budgets and attribution settings?',
    answer:
      "Attribution Settings:\n\n1. Meta: 7-day click, 1-day view\n2. Google: 30-day click attribution\n\nSuggested Budget: 3–5% of the brand's monthly GMV or a minimum of ₹3 lakhs (whichever is higher).",
  },
  {
    value: 'faq-7',
    question: 'How do I get started?',
    answer:
      "Here's your quick-start checklist:\n\na. Link your Business Manager ID and access your brand catalogue.\nb. Create a dedicated ad account for Blinkit campaigns and share view access with Blinkit's BM ID: 378533195675277.\nc. Fetch relevant audiences from the MediaMax audience tool.\nd. Curate your landing page.\ne. Launch the campaign with proper campaign setups.",
    hasGetStartedLink: true,
  },
  {
    value: 'faq-8',
    question: 'How does tracking work and how are ads delivered?',
    answer:
      "No additional pixel or SDK setup is required. MediaMax uses Blinkit's integrated catalogue signals to automatically track product views, add-to-carts, and purchases. Ads are served dynamically based on:\n\n• Blinkit's real-time deliverability\n• Product stock availability (ads will not show in out-of-stock regions)",
  },
  {
    value: 'faq-9',
    question: 'How can I measure campaign performance?',
    answer:
      'You can access real-time performance dashboards with metrics such as impressions, clicks, ROAS, GMV, conversions, and spend across Meta and Google platforms.',
  },
  {
    value: 'faq-10',
    question: 'How long does campaign approval take?',
    answer:
      'Campaigns are typically reviewed and approved within 24 hours once all assets (catalogue, audiences, creatives) are in place.',
  },
  {
    value: 'faq-11',
    question: 'Can multiple brands or categories under one manufacturer use MediaMax?',
    answer:
      'Yes. Multi-brand or multi-category manufacturers can onboard each brand individually under their business manager and run parallel or cross-category campaigns within the same dashboard.',
  },
  {
    value: 'faq-12',
    question: 'What support is available if I face issues?',
    answer:
      "Blinkit's MediaMax support team and Meta/Google platform partners are available to assist with onboarding, troubleshooting, and optimisation queries.",
  },
]

// General MediaMax FAQs: the landing page and the public /faqs page.
export const MEDIAMAX_FAQ_ITEMS = [
  {
    value: 'mediamax-faq-1',
    question: 'What is MediaMax?',
    answer:
      "MediaMax is Blinkit's offsite media product. It helps brands bring traffic from outside Blinkit, such as Meta, Google, creators and owned channels, to Blinkit, and measure what customers did after the click, through to purchase.",
  },
  {
    value: 'mediamax-faq-2',
    question: 'Why should I use MediaMax? How is it different from traditional Meta or Google ads?',
    answer:
      "MediaMax offers different products that you can use based on your objectives.\n\nMeta and Google Ads with Blinkit let you run commerce-driven campaigns that leverage Blinkit's data and tech to deliver highly targeted ads in sync with your inventory on Blinkit.\n\nShoppable Links complement your campaigns outside the Blinkit app by letting you create trackable landing pages and corresponding links.",
  },
  {
    value: 'mediamax-faq-3',
    question: 'Which MediaMax product should I use?',
    answer:
      "Meta CPAS with Blinkit: use it when you want to run performance campaigns on Meta leveraging Blinkit's tech and data intelligence.\n\nGoogle CMN with Blinkit (launching soon): use it when you want to run performance campaigns on Google leveraging Blinkit's tech and data intelligence.\n\nShoppable Links: use it for traffic from any source, such as creators, influencers, owned channels and partnerships, or Meta and Google ads outside CPAS and CMN, sent to a landing page you build.",
  },
  {
    value: 'mediamax-faq-4',
    question: "Can I target Blinkit's audiences?",
    answer:
      "Yes, with Meta and Google Ads with Blinkit, you can build your targeting around Blinkit's high-intent shared audiences. Audience targeting is not part of Shoppable Links.",
  },
  {
    value: 'mediamax-faq-5',
    question: 'How do I get started?',
    answer:
      "Select the brands you handle on Blinkit to request access. Once you have access, you can proceed with the relevant solution. For Meta, you need to link your Business Manager (BM) with Blinkit's BM. For Shoppable Links, no integration is required, and you can get started by creating links.",
  },
  {
    value: 'mediamax-faq-6',
    question: 'Are there any additional charges to use MediaMax?',
    answer: 'No, there are no additional charges to use MediaMax.',
  },
  {
    value: 'mediamax-faq-7',
    question: 'How can I measure performance?',
    answer:
      'For Meta and Google Ads with Blinkit, refer to the respective ad platform, since these are enabled through an integration between Blinkit and Meta or Google. For Shoppable Links, every brand has a page listing all its links with their metrics.',
  },
  {
    value: 'mediamax-faq-8',
    question: 'What support is available if I face issues?',
    answer: 'Write to mediamax@blinkit.com or get in touch with your Blinkit account manager.',
  },
]

// The landing page shows the first five; "View All" opens the full list.
export const HOME_FAQ_ITEMS = MEDIAMAX_FAQ_ITEMS.slice(0, 5)

// Leading Partners Constants
export const SIXTY_PLUS_LEADING_BRANDS_HAVE = '200+ leading brands have'
export const IMPROVED_THEIR_ROAS_USING = 'improved their ROAS using'
export const MEDIAMAX = 'MediaMax'
export const POWERED_BY_BLINKIT_SHOPPER_AUDIENCES =
  'Powered by Blinkit shopper audiences, MediaMax helps you activate high-intent, category-specific'
export const AUDIENCES_DIRECTLY_INTO_YOUR_BRANDS_AD_ACCOUNT =
  "audiences directly into your brand's ad account"
export const BOOST_MY_ADS_WITH_MEDIAMAX = 'Boost my ads with MediaMax'

export const PARTNER_LOGOS_META = [
  {
    id: 'many-poko-pants',
    alt: 'Mamy Poko Pants logo',
    leftClass: 'left-0',
    zClass: 'z-30',
  },
  {
    id: 'the-face-shop',
    alt: 'The Face Shop logo',
    leftClass: 'left-12',
    zClass: 'z-20',
  },
  {
    id: 'paradyes',
    alt: 'Paradyes logo',
    leftClass: 'left-24',
    zClass: 'z-10',
  },
] as const

// Testimonials Constants
export const WE_JUST_ASKED_THEM_WHAT_THEY_TRULY_FELT = 'We just asked them what they truly felt'
export const TESTIMONIALS_LABEL = 'TESTIMONIALS'
export const TESTIMONIALS_HEADING = 'What Our Client Said about us'
export const HOME_TESTIMONIALS_COUNT = 3
export const TESTIMONIALS_DATA = [
  {
    logo: 'M',
    companyName: 'Brand Partner',
    text: "MediaMax transformed our campaigns. The precision targeting using Blinkit's first-party data delivered results we hadn't seen before. Our team couldn't be happier with the platform's performance.",
    name: 'Michael Lee',
    title: 'CEO, Plum',
    metric: '90%',
    metricLabel: 'ROAS increased',
  },
  {
    logo: 'J',
    companyName: 'Brand Partner',
    text: 'The platform gave us real-time access to Blinkit shopper audiences. From website design to PPC campaigns, they deliver consistently results, making MediaMax highly recommendable.',
    name: 'Jessica Taylor',
    title: 'Director of Marketing, Anveshan',
    metric: '81%',
    metricLabel: 'Mid-funnel ROAS improvement',
  },
  {
    logo: 'D',
    companyName: 'Brand Partner',
    text: 'Very satisfied with the Meta and Google ad services. They improved our brand visibility online and provided valuable insights. The live catalog sync feature makes running campaigns incredibly efficient.',
    name: 'David Smith',
    title: 'CEO, sample company name',
    metric: '94%',
    metricLabel: 'Revenue per user uplift',
  },
]

// Workflow Text Constants
export const HERES_HOW_MEDIAMAX_FITS_INTO_YOUR_AD_WORKFLOW =
  "HERE'S HOW MEDIAMAX FITS INTO YOUR AD WORKFLOW"
export const MEDIAMAX_PROVIDES_YOU_THE = 'MediaMax provides you the'
export const RIGHT_TOOLS_TO_MAKE_YOUR_DIGITAL_CAMPAIGNS =
  'right tools to boost your digital campaigns'

// Hero CTA
export const HERO_GET_STARTED = 'Get started'

// CTA Banner Section Constants
export const CTA_BADGE = 'Trusted by 350+ brands driving incremental growth'
export const CTA_HEADING = 'Ready to turn Blinkit demand into growth?'
export const CTA_SUBTITLE = 'Join the brands reaching millions of high-intent shoppers'
export const CTA_GET_STARTED = 'Get Started'

// Hero Rotating Text Constants
export const HERO_ROTATING_TEXTS = ['Go Social', 'Scale Revenue', 'Drive Orders', 'Convert Clicks']
export const HERO_SUBTITLE = 'Turn every impression into intent with blinkit audience'
export const HERO_HEADING_SUFFIX = 'with Mediamax'

// Home Stats Constants
export const HOME_STATS = [
  { value: '350+', label: 'Brands onboarded' },
  { value: '2.5x+', label: 'Average ROAS' },
  { value: '26M+', label: 'Monthly clicks driven' },
  { value: '5B+', label: 'Monthly impressions' },
] as const

// Platform Features Section Constants
export const PLATFORM_FEATURES_LABEL = 'PLATFORM FEATURES'
export const PLATFORM_FEATURES_HEADING = 'Everything you need to scale your social growth'
export const COMING_SOON_LABEL = 'COMING SOON'

export const PLATFORM_FEATURES_LIST: readonly PlatformFeature[] = [
  {
    title: 'Ad Account Linking',
    description: 'Connect your Meta & Google ad accounts in minutes',
  },
  {
    title: 'Live Catalog Sync',
    description: 'Keep your ads always in sync with live inventory',
  },
  {
    title: 'Blinkit Audiences',
    description: "Reach high-intent shoppers using Blinkit's first-party audiences",
  },
  {
    title: 'Blinkit Landing Page',
    description: 'Own the experience with your landing pages',
  },
  {
    title: 'Tracking & Insight',
    description: 'Performance insights to help you scale smarter and faster',
  },
]

// Solutions Section Constants
export const SOLUTIONS_LABEL = 'SOLUTIONS'
export const SOLUTIONS_HEADING = 'Two ways to drive traffic to Blinkit'
export const INTEGRATED_SOLUTIONS_GROUP = {
  title: 'Integrated solutions',
  subtitle: 'Leverage Blinkit’s data & tech to power your ads on Meta & Google',
} as const
export const SHOPPABLE_LINKS_GROUP = {
  title: 'Shoppable links',
  subtitle: 'Send traffic from anywhere to Blinkit',
} as const
export const META_CARD_DATA = {
  title: 'Reach & convert across the full funnel',
  features: ['Inspire discovery', 'Reach new audiences', 'Drive consideration', 'Re-engage users'],
} as const
export const GOOGLE_CARD_DATA = {
  title: 'Capture high-intent demand at scale',
  features: ['High-intent users', 'In-market demand', 'Action-driven', 'Conversions'],
  comingSoon: true,
} as const
export const SHOPPABLE_LINKS_CARD_DATA = {
  title: 'Measure the impact of your media',
  features: [
    'Creators & influencers',
    'Any traffic source',
    'Link-level performance',
    'Bottom-funnel metrics',
  ],
} as const
export const SHOPPABLE_LINKS_LOGO_LABEL = 'Shoppable Links'
